import axios from 'axios';
import type { CredenciaisLoginLojista, RespostaAutenticacaoCentral } from '../tipos';

/**
 * Serviço de autenticação centralizada para lojistas
 * Requisitos: 1.2, 1.6, 6.1, 6.2, 6.3, 6.5
 *
 * - Login via POST /api/central/auth/login no servidor central
 * - Armazena token JWT, expiração e dados do lojista no localStorage
 * - Restaura sessão a partir do localStorage com validação de expiração
 * - Logout limpa todos os dados de sessão
 * - Timeout de 10 segundos nas requisições
 */

const CHAVE_TOKEN = 'nexo_token';
const CHAVE_TOKEN_EXPIRACAO = 'nexo_token_expiracao';
const CHAVE_LOJISTA = 'nexo_lojista';
const CHAVE_ULTIMO_ESTABELECIMENTO = 'nexo_ultimo_estabelecimento_id';

const TIMEOUT_MS = 10_000;

class ServicoAutenticacao {
  private readonly urlBase: string;

  constructor() {
    this.urlBase = import.meta.env.VITE_API_CENTRAL_URL || '';
  }

  /**
   * Realiza login contra o servidor central.
   * Envia POST para /api/central/auth/login com usuário e senha.
   * Armazena token, expiração e dados do lojista no localStorage.
   */
  async loginCentral(
    credenciais: CredenciaisLoginLojista
  ): Promise<RespostaAutenticacaoCentral> {
    const resposta = await axios.post(
      `${this.urlBase}/api/central/auth/login`,
      {
        usuario: credenciais.usuario,
        senha: credenciais.senha,
      },
      {
        timeout: TIMEOUT_MS,
        headers: { 'Content-Type': 'application/json' },
      }
    );

    const apiData = resposta.data as {
      token: string;
      expires_in: number;
      lojista: { id: string; nome: string; email: string; eh_admin?: boolean };
      estabelecimentos: Array<{
        id: string;
        nome: string;
        ponto_acesso_api: string;
        url_logo: string | null;
        cores_customizadas: {
          cor_primaria?: string;
          cor_secundaria?: string;
          cor_destaque?: string;
          cor_texto?: string;
          cor_fundo?: string;
        } | null;
      }>;
    };

    const dados: RespostaAutenticacaoCentral = {
      token: apiData.token,
      expiraEm: apiData.expires_in,
      lojista: {
        id: apiData.lojista.id,
        nome: apiData.lojista.nome,
        email: apiData.lojista.email,
        ehAdmin: apiData.lojista.eh_admin ?? false,
      },
      estabelecimentos: apiData.estabelecimentos.map((est) => ({
        id: est.id,
        nome: est.nome,
        pontoAcessoApi: est.ponto_acesso_api,
        customizacao: {
          urlLogo: est.url_logo ?? '',
          nomeEstabelecimento: est.nome,
          corPrimaria: est.cores_customizadas?.cor_primaria ?? '#568203',
          corSecundaria: est.cores_customizadas?.cor_secundaria ?? '#32CD32',
          corDestaque: est.cores_customizadas?.cor_destaque ?? '#32CD32',
          corTexto: est.cores_customizadas?.cor_texto ?? '#ffffff',
          corFundo: est.cores_customizadas?.cor_fundo ?? '#f4f7f0',
        },
      })),
    };

    this.armazenarSessao(dados);
    return dados;
  }

  /**
   * Restaura sessão a partir do localStorage.
   * Verifica se o token existe e se a expiração é válida (futura).
   * Retorna null se não houver sessão válida ou dados estiverem corrompidos.
   */
  restaurarSessao(): RespostaAutenticacaoCentral | null {
    try {
      const token = localStorage.getItem(CHAVE_TOKEN);
      const expiracao = localStorage.getItem(CHAVE_TOKEN_EXPIRACAO);
      const lojistaJson = localStorage.getItem(CHAVE_LOJISTA);

      if (!token || !expiracao || !lojistaJson) {
        this.logout();
        return null;
      }

      const dataExpiracao = new Date(expiracao);
      if (isNaN(dataExpiracao.getTime()) || dataExpiracao <= new Date()) {
        this.logout();
        return null;
      }

      const lojista = JSON.parse(lojistaJson);
      if (!lojista || !lojista.id || !lojista.nome || !lojista.email) {
        this.logout();
        return null;
      }

      // Calcula expiraEm em segundos restantes
      const expiraEm = Math.floor(
        (dataExpiracao.getTime() - Date.now()) / 1000
      );

      return {
        token,
        expiraEm,
        lojista: {
          id: lojista.id,
          nome: lojista.nome,
          email: lojista.email,
          ehAdmin: lojista.ehAdmin ?? false,
        },
        estabelecimentos: [], // Estabelecimentos são gerenciados pela fatiaEstabelecimento
      };
    } catch {
      // JSON inválido ou qualquer erro — descarta sessão
      this.logout();
      return null;
    }
  }

  /**
   * Realiza logout limpando todos os dados de sessão do localStorage.
   */
  logout(): void {
    localStorage.removeItem(CHAVE_TOKEN);
    localStorage.removeItem(CHAVE_TOKEN_EXPIRACAO);
    localStorage.removeItem(CHAVE_LOJISTA);
    localStorage.removeItem(CHAVE_ULTIMO_ESTABELECIMENTO);
  }

  /**
   * Retorna o token JWT atual armazenado no localStorage.
   * Retorna null se não houver token ou se estiver expirado.
   */
  obterTokenAtual(): string | null {
    const token = localStorage.getItem(CHAVE_TOKEN);
    if (!token) return null;

    if (!this.estaAutenticado()) {
      this.logout();
      return null;
    }

    return token;
  }

  /**
   * Verifica se o usuário está autenticado (token existe e não expirou).
   */
  estaAutenticado(): boolean {
    const token = localStorage.getItem(CHAVE_TOKEN);
    if (!token) return false;

    const expiracao = localStorage.getItem(CHAVE_TOKEN_EXPIRACAO);
    if (!expiracao) return false;

    const dataExpiracao = new Date(expiracao);
    if (isNaN(dataExpiracao.getTime())) return false;

    return dataExpiracao > new Date();
  }

  /**
   * Armazena token, expiração e dados do lojista no localStorage.
   */
  private armazenarSessao(dados: RespostaAutenticacaoCentral): void {
    const dataExpiracao = new Date(Date.now() + dados.expiraEm * 1000);
    localStorage.setItem(CHAVE_TOKEN, dados.token);
    localStorage.setItem(CHAVE_TOKEN_EXPIRACAO, dataExpiracao.toISOString());
    localStorage.setItem(CHAVE_LOJISTA, JSON.stringify(dados.lojista));
  }
}

export const servicoAutenticacao = new ServicoAutenticacao();
